import { assign, createMachine } from "xstate4"
import {
  GenerateFormData,
  ImportableCollection,
  UploadFormData,
  UploadRecord,
  ValidationResult
} from "../types"
import {
  getLoginLettersData,
  generateLoginLetters,
  importUsers
} from "../manageActions"

type Context = {
  collection: ImportableCollection
  districtId: string
  schoolId: string
  state: string
  districtName: string
  schoolName: string
  title: string
  uploadData: UploadFormData
  uploading: boolean
  errors: ValidationResult[]
  progress: number
  count: number
  total: number
  importedRecords: UploadRecord[]
  duplicates: any[]
  generating: boolean
  generateData: GenerateFormData
  letterData: UploadRecord[]
  letterUrl: string
  feedback: {
    type: "success" | "error"
    message: string
  }
}

type ImportEvent = { type: "IMPORT"; uploadData: UploadFormData }
type ImportProgressEvent = {
  type: "IMPORT_PROGRESS"
  progress: number
  count: number
  record?: UploadRecord
}
type ImportErrorEvent = {
  type: "IMPORT_ERROR"
  errors?: ValidationResult[]
  duplicates?: any[]
}
type ImportCompleteEvent = { type: "IMPORT_COMPLETE" }
type GenerateEvent = { type: "GENERATE"; data: GenerateFormData }
type GenerateCompleteEvent = {
  type: "done.invoke.generateLoginLetters"
  data: {
    letterData: UploadRecord[]
    letterUrl: string
  }
}
type EventObject =
  | ImportEvent
  | ImportProgressEvent
  | ImportErrorEvent
  | ImportCompleteEvent
  | GenerateEvent

const importUsersMachine = createMachine<Context, EventObject>(
  {
    id: "import",
    initial: "select",
    schema: {
      context: {} as Context,
      events: {} as EventObject
    },
    context: {
      collection: "" as ImportableCollection,
      districtId: "",
      schoolId: "",
      state: "",
      districtName: "",
      schoolName: "",
      title: "Import .csv",
      uploadData: null,
      uploading: false,
      errors: [],
      progress: 0,
      count: 0,
      total: 0,
      importedRecords: [],
      duplicates: [],
      generating: false,
      generateData: null,
      letterData: [],
      letterUrl: "",
      feedback: null
    },
    states: {
      select: {
        entry: assign<Context>({ title: "Import .csv" }),
        on: {
          IMPORT: {
            target: "uploading",
            actions: assign<Context>((_, { uploadData }: ImportEvent) => ({
              uploadData,
              total: uploadData.data.length
            }))
          }
        }
      },
      uploading: {
        invoke: {
          src: "uploadImportData",
          onError: {
            actions: assign<Context>((_ctx, event: any) => ({
              feedback: {
                type: "error",
                message: event.data.message
              }
            }))
          }
        },
        entry: [
          assign<Context>({
            title: ctx => `Importing "${ctx.uploadData?.file[0]?.name ?? ""}"`,
            uploading: true,
            errors: []
          })
        ],
        on: {
          IMPORT_PROGRESS: {
            actions: assign<Context>(
              (ctx, { progress, count, record }: ImportProgressEvent) => ({
                progress,
                count,
                importedRecords: record
                  ? [...ctx.importedRecords, record]
                  : ctx.importedRecords
              })
            )
          },
          IMPORT_ERROR: {
            actions: assign<Context>(
              (ctx, { errors, duplicates }: ImportErrorEvent) => ({
                errors: errors ? [...ctx.errors, ...errors] : ctx.errors,
                duplicates: duplicates
                  ? [...ctx.duplicates, ...duplicates]
                  : ctx.duplicates
              })
            )
          },
          IMPORT_COMPLETE: {
            target: "generate",
            actions: assign<Context>({
              feedback: {
                type: "success",
                message: "Import complete."
              }
            })
          }
        },
        exit: assign<Context>({ uploading: false })
      },
      generate: {
        entry: assign<Context>({ title: "Generate Login Letters" }),
        on: {
          GENERATE: {
            target: "generating",
            actions: assign<Context>((_ctx, { data }: GenerateEvent) => ({
              generateData: data
            }))
          }
        }
      },
      generating: {
        invoke: {
          id: "generateLoginLetters",
          src: "generateLoginLetters",
          onDone: {
            target: "complete",
            actions: assign<Context>(
              (
                _ctx,
                { data: { letterData, letterUrl } }: GenerateCompleteEvent
              ) => ({
                feedback: {
                  type: "success",
                  message: "Generate complete."
                },
                letterData,
                letterUrl
              })
            )
          },
          onError: {
            target: "complete"
          }
        },
        entry: assign<Context>({ generating: true }),
        exit: assign<Context>({ generating: false })
      },
      complete: {
        entry: assign<Context>({ title: "" })
      }
    }
  },
  {
    services: {
      uploadImportData: ctx => sendBack => {
        const { collection, districtId, schoolId, state, uploadData } = ctx

        importUsers(
          collection,
          uploadData.data,
          { districtId, schoolId, state },
          {
            onProgress: (count, progress, record) =>
              sendBack({ type: "IMPORT_PROGRESS", count, progress, record }),
            onError: (errors, duplicates) =>
              sendBack({ type: "IMPORT_ERROR", errors, duplicates }),
            onComplete: () => sendBack({ type: "IMPORT_COMPLETE" })
          }
        )
      },
      generateLoginLetters: async ({
        collection,
        importedRecords,
        duplicates,
        generateData,
        districtName,
        schoolName,
        districtId,
        schoolId,
        state
      }) => {
        const letterData = await getLoginLettersData(
          collection,
          importedRecords,
          duplicates,
          generateData,
          { districtId, schoolId, state }
        )

        const { url: letterUrl } = await generateLoginLetters(
          letterData,
          districtName,
          schoolName,
          state
        )

        return { letterUrl, letterData }
      }
    }
  }
)

export default importUsersMachine
